/* eslint-disable global-require */

import { HealthState } from '@/lib/calculationstate';

interface Testimonial {
  name: string;
  icon: string;

  text: TestimonialText;
  state: TestimonialState;
}

interface TestimonialText {
  intro: string;
  disability: string;
  salary: string;

  popup: {
    job: string;

    reason: string;
    decision: string;
    income: string;
  };
}

interface TestimonialState {
  age: number;
  disabilityRatio: number;
  situation: HealthState;
  salary: number;
  extraIncome: number;
  cannotRecover: boolean;
}

const hans: Testimonial = {
  name: 'Hans',
  icon: require('@/assets/testimonials/hans.png'),

  text: {
    intro: 'Door een burn-out kan Hans niet meer werken als bedrijfsleider.',
    disability: 'Na zijn burn-out is hij <strong>volledig</strong> arbeidsongeschikt geworden.',
    salary: 'Op dit moment heeft Hans geen inkomen naast zijn uitkering.',

    popup: {
      job: `Bedrijfsleider bij een bedrijf gespecialiseerd in isolatietechnieken.

      € 87.948 bruto per jaar inclusief vakantiegeld en 13e maand op basis van een 40-urige werkweek`,
      reason: 'Burn-out',
      decision: `Hans is 80% arbeidsongeschikt.
      Het UWV besluit dat hij volledig, maar niet-duurzaam arbeidsongeschikt is.
      Zij denken dat Hans nog gedeeltelijk of volledig kan herstellen.`,
      income: `Na 2 jaar (104 weken) kan Hans nog helemaal niet werken.
      Hij ontvangt geen loon uit arbeid meer.
      De arbeidsovereenkomst tussen Hans en zijn werkgever wordt beëindigd.`,
    },
  },

  state: {
    age: 50,
    situation: HealthState.LongSick,
    salary: 87_948,
    disabilityRatio: 0.8,
    extraIncome: 0,
    cannotRecover: false,
  },
};

const timo: Testimonial = {
  name: 'Timo',
  icon: require('@/assets/testimonials/timo.png'),

  text: {
    intro: 'Na een bedrijfsongeval kan Timo zijn hand niet meer volledig gebruiken.',
    disability: 'Na een bedrijfsongeval werd hij <strong>gedeeltelijk</strong> arbeidsongeschikt.',
    salary: 'Door zijn uitvalsverschijnselen kan Timo niet meer werken als metaalbewerker',

    popup: {
      job: `Metaalbewerker.

      € 33.504 bruto per jaar inclusief vakantiegeld en vaste winstuitkering op basis van een
      40-urige werkweek.`,
      reason: 'Carpaal tunnelsyndroom (beknelling van een zenuw in de arm)',
      decision: `Volgens het UWV kan Timo met ander werk nog 40% van zijn oude loon verdienen.
      Het UWV besluit daarom dat hij voor 60% arbeidsongeschikt is.`,
      income: `Timo kan niet meer werken als metaalbewerker.
      Wel zou hij een administratieve functie kunnen uitoefenen.
      Zulke functies heeft zijn werkgever alleen niet beschikbaar.
      Daarom wordt de arbeidsovereenkomst na 2 jaar (104 weken) ziekte beëindigd.`,
    },
  },

  state: {
    age: 29,
    situation: HealthState.LongSick,
    salary: 33_504,
    disabilityRatio: 0.6,
    extraIncome: 0,
    cannotRecover: false,
  },
};

const janny: Testimonial = {
  name: 'Janny',
  icon: require('@/assets/testimonials/janny.png'),

  text: {
    intro: 'Door rugklachten kan Janny niet meer hele dagen zitten.',
    disability: 'Door haar rugklachten is ze <strong>beperkt</strong> arbeidsongeschikt.',
    salary: 'Janny werkt nu 6 uur per dag en verdient 75% van haar laatste loon.',

    popup: {
      job: `Receptioniste bij een autobedrijf.

      € 29.808 bruto per jaar inclusief vakantiegeld op basis van een 32-urige werkweek.`,
      reason: 'Rugklachten',
      decision: `UWV denkt dat Janny minder uren kan gaan werken.
      Dan kan ze nog 75% van haar oude loon verdienen.
      Ze is daarom 25% arbeidsongeschikt.
      Dit betekent dat ze geen uitkering ontvangt van UWV,
      omdat ze minder dan 35% arbeidsongeschikt is.`,
      income: `Door de fysiotherapie gaat het al veel beter met Janny.
      Na 2 jaar (104 weken), kan zij geen volledige dagen werken,
      maar wel 6 uur op een dag.
      Ze verdient nu 75% van haar oude loon.`,
    },
  },

  state: {
    age: 43,
    situation: HealthState.LongSick,
    salary: 29_808,
    disabilityRatio: 0.25,
    extraIncome: 22_356,
    cannotRecover: false,
  },
};

const youssef: Testimonial = {
  name: 'Youssef',
  icon: require('@/assets/testimonials/youssef.png'),

  text: {
    intro: 'Heeft een gecompliceerde beenbreuk opgelopen door een auto ongeluk.',
    disability: 'Als gevolg van een ongeluk werd hij <strong>gedeeltelijk</strong> arbeidsongeschikt.',
    salary: 'Door halve dagen te gaan werken verdient hij nu 50% van zijn oude loon.',

    popup: {
      job: `Productiemedewerker.

      € 25.920 bruto per jaar inclusief vakantiegeld op basis van een 38-urige werkweek.`,
      reason: 'Gecompliceerde beenbreuk',
      decision: `Na het ongeluk viel Youssef even volledig uit.
      Twee jaar (104 weken) en verschillende operaties later,
      kan hij weer gedeeltelijk werken.
      Het UWV denkt dat hij nog 40% van zijn oude loon kan verdienen.
      Hij is daarom 60% arbeidsongeschikt.`,
      income: `Twee jaar (104 weken) na het ongeluk kan hij nog niet volledig werken.
      Bij zijn werkgever kan hij nu halve dagen werken en kan hij aangepast werk doen.
      Youssef verdient nu 50% van zijn oude loon.`,
    },
  },

  state: {
    age: 31,
    situation: HealthState.LongSick,
    salary: 25_920,
    disabilityRatio: 0.6,
    extraIncome: 12_960,
    cannotRecover: false,
  },
};

export default {
  hans,
  timo,
  janny,
  youssef,
};
